"use client";

/**
 * AdminLocationProvider — Non-Intrusive Viewport Location Security Gate
 *
 * ARCHITECTURE:
 *  1. Non-disruptive Layout: Does NOT push the Header or content down. The gate sits
 *     in a centered fixed viewport overlay (`fixed inset-0 z-50`).
 *  2. Interactive Shield: Until location access is resolved, the underlying Admin shell
 *     is rendered non-interactive (`pointer-events-none`, `aria-hidden`), blocking all
 *     sidebar navigation, forms, mutations, and settings per product security policy.
 *  3. Comprehensive State Handling:
 *     - "prompt": Friendly administrative explanation with "Allow Location Access" button.
 *     - "granted": Gate dismisses, location saved to MongoDB and session cookie, Admin interactive.
 *     - "denied": Clear browser-settings recovery guide (site permissions), Admin remains blocked.
 *     - "unavailable": Inability to access device location sensor explained.
 *  4. Session Persistence: Remembers verified permission in sessionStorage to prevent
 *     unnecessary repeated browser prompts across internal App Router navigations.
 */

import * as React from "react";
import { useRouter } from "next/navigation";
import {
  MapPin,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  RotateCcw,
  LogOut,
  HelpCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  buildLocationFromBigDataCloud,
  formatLocationDisplay,
} from "@/lib/admin/location-formatter";

export interface ClientLocationState {
  status: "available" | "denied" | "unavailable";
  latitude?: number;
  longitude?: number;
  address?: string;
  locality?: string;
  city?: string;
  region?: string;
  country?: string;
  postalCode?: string;
  source?: string;
  capturedAt?: string;
}

export type GateStatus = "prompt" | "granted" | "denied" | "unavailable";

const LocationContext = React.createContext<{
  location: ClientLocationState | null;
  isGated: boolean;
  requestLocation: () => Promise<void>;
}>({
  location: null,
  isGated: false,
  requestLocation: async () => {},
});

export function useAdminLocation() {
  return React.useContext(LocationContext);
}

export function AdminLocationProvider({
  children,
}: {
  children?: React.ReactNode;
}) {
  const router = useRouter();
  const [gateStatus, setGateStatus] = React.useState<GateStatus>("prompt");
  const [isGated, setIsGated] = React.useState<boolean>(false);
  const [requesting, setRequesting] = React.useState<boolean>(false);
  const [showExplanation, setShowExplanation] = React.useState<boolean>(false);
  const [location, setLocation] = React.useState<ClientLocationState | null>(null);

  const saveLocationOnServer = React.useCallback(
    async (loc: ClientLocationState) => {
      try {
        setLocation(loc);
        if (typeof window !== "undefined") {
          (window as any).__ADMIN_LOCATION__ = loc;
        }
        await fetch("/api/admin/security/location", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(loc),
        });
      } catch {
        // Silently catch network errors
      }
    },
    []
  );

  const obtainBrowserLocation = React.useCallback(async () => {
    if (typeof window === "undefined" || !navigator.geolocation) {
      setGateStatus("unavailable");
      setIsGated(true);
      await saveLocationOnServer({ status: "unavailable" });
      return;
    }

    setRequesting(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = Number(pos.coords.latitude.toFixed(5));
        const lon = Number(pos.coords.longitude.toFixed(5));

        let resolvedLoc: ClientLocationState = {
          status: "available",
          latitude: lat,
          longitude: lon,
          source: "browser-geolocation",
          capturedAt: new Date().toISOString(),
        };

        try {
          const cachedRaw = sessionStorage.getItem("admin_location_cache");
          let needLookup = true;
          if (cachedRaw) {
            const cached = JSON.parse(cachedRaw);
            // Threshold caching: if within ~0.005 degrees (~500m), reuse reverse geocoding
            if (
              cached &&
              typeof cached.latitude === "number" &&
              typeof cached.longitude === "number" &&
              Math.abs(cached.latitude - lat) < 0.005 &&
              Math.abs(cached.longitude - lon) < 0.005
            ) {
              needLookup = false;
              resolvedLoc = {
                ...cached,
                latitude: lat,
                longitude: lon,
                capturedAt: new Date().toISOString(),
              };
            }
          }

          if (needLookup) {
            // Live reverse geocoding via BigDataCloud client API
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 4000);
            const res = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`,
              { signal: controller.signal }
            );
            clearTimeout(timeout);

            if (res.ok) {
              const geoData = await res.json();
              const structured = buildLocationFromBigDataCloud(lat, lon, geoData);
              resolvedLoc = {
                ...structured,
                status: "available",
              };
              sessionStorage.setItem(
                "admin_location_cache",
                JSON.stringify(resolvedLoc)
              );
            }
          }
        } catch (e) {
          // Graceful fallback: retain coordinates without crashing
          console.warn("[AdminLocationProvider] Reverse geocode fallback:", e);
        }

        await saveLocationOnServer(resolvedLoc);
        setRequesting(false);
        setGateStatus("granted");
        setIsGated(false);
        try {
          sessionStorage.setItem("admin_location_verified", "true");
        } catch {}
      },
      async (err) => {
        setRequesting(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGateStatus("denied");
          setIsGated(true);
          await saveLocationOnServer({ status: "denied" });
        } else {
          setGateStatus("unavailable");
          setIsGated(true);
          await saveLocationOnServer({ status: "unavailable" });
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, [saveLocationOnServer]);

  // Initial Permission Check
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const verified = sessionStorage.getItem("admin_location_verified");
      if (verified === "true") {
        setIsGated(false);
        setGateStatus("granted");
        return;
      }
    } catch {}

    // Check browser permission status if Permissions API is supported
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "geolocation" as PermissionName })
        .then((permissionStatus) => {
          if (permissionStatus.state === "granted") {
            obtainBrowserLocation();
          } else if (permissionStatus.state === "denied") {
            setGateStatus("denied");
            setIsGated(true);
          } else {
            // 'prompt'
            setGateStatus("prompt");
            setIsGated(true);
          }

          permissionStatus.onchange = () => {
            if (permissionStatus.state === "granted") {
              obtainBrowserLocation();
            } else if (permissionStatus.state === "denied") {
              setGateStatus("denied");
              setIsGated(true);
            }
          };
        })
        .catch(() => {
          setGateStatus("prompt");
          setIsGated(true);
        });
    } else {
      setGateStatus("prompt");
      setIsGated(true);
    }
  }, [obtainBrowserLocation]);

  const handleLogout = async () => {
    try {
      await fetch("/api/admin/auth/logout", { method: "POST" });
    } catch {}
    router.push("/admin/login");
    router.refresh();
  };

  return (
    <LocationContext.Provider
      value={{
        location,
        isGated,
        requestLocation: obtainBrowserLocation,
      }}
    >
      {/* Underlying Admin Shell: stays visually in place, but inert when gated */}
      <div
        className={`relative flex min-h-screen flex-col transition-opacity duration-200 ${
          isGated ? "pointer-events-none select-none opacity-50" : ""
        }`}
        aria-hidden={isGated ? "true" : undefined}
      >
        {children}
      </div>

      {/* Centered Location Security Gate Overlay */}
      {isGated && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="location-gate-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/50 backdrop-blur-xs p-4 duration-200 animate-in fade-in-0 select-none"
        >
          <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-7 shadow-2xl dark:border-neutral-800 dark:bg-neutral-900 text-center transition-all">
            {/* Icon Header */}
            <div className="flex justify-center mb-4">
              <div
                className={`flex h-14 w-14 items-center justify-center rounded-2xl border ${
                  gateStatus === "denied"
                    ? "bg-red-50 text-red-600 border-red-200 dark:bg-red-950/40 dark:border-red-900/50 dark:text-red-400"
                    : gateStatus === "unavailable"
                    ? "bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-950/40 dark:border-amber-900/50 dark:text-amber-400"
                    : "bg-cyan-50 text-cyan-600 border-cyan-200 dark:bg-cyan-950/40 dark:border-cyan-900/50 dark:text-cyan-400"
                }`}
              >
                {gateStatus === "denied" ? (
                  <ShieldAlert className="h-7 w-7" />
                ) : gateStatus === "unavailable" ? (
                  <AlertTriangle className="h-7 w-7" />
                ) : (
                  <MapPin className="h-7 w-7" />
                )}
              </div>
            </div>

            {/* Title & Description */}
            <h2
              id="location-gate-title"
              className="text-lg font-bold text-neutral-900 dark:text-white tracking-tight"
            >
              {gateStatus === "denied"
                ? "Location Access Blocked"
                : gateStatus === "unavailable"
                ? "Location Sensor Unavailable"
                : "Location Access Required"}
            </h2>

            <p className="mt-2 text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-sm mx-auto">
              {gateStatus === "denied"
                ? "Your browser has blocked location access. In accordance with security policy, console operations remain restricted until access is granted."
                : gateStatus === "unavailable"
                ? "Unable to determine device location coordinates. Please verify your operating system location privacy settings."
                : "Allow location access to enable location-aware audit logging and continue using the Admin Control Center."}
            </p>

            {/* Step-by-Step Browser Recovery Guide for Denied state */}
            {gateStatus === "denied" && (
              <div className="mt-4 text-left rounded-xl border border-neutral-200 bg-neutral-50/60 p-3.5 text-xs text-neutral-600 dark:border-neutral-800 dark:bg-neutral-950/50 dark:text-neutral-400 space-y-2">
                <div className="font-semibold text-neutral-900 dark:text-white text-[11px] uppercase tracking-wider">
                  How to unblock in your browser:
                </div>
                <ol className="list-decimal list-inside space-y-1 text-[11px] leading-relaxed">
                  <li>
                    Click the <strong>Site Settings / Padlock</strong> icon in your browser URL bar.
                  </li>
                  <li>
                    Change <strong>Location</strong> permission from <em>Block</em> to <em>Allow</em>.
                  </li>
                  <li>
                    Click <strong>Check Again</strong> below.
                  </li>
                </ol>
              </div>
            )}

            {/* Collapsible "Why We Need This" for Prompt state */}
            {gateStatus === "prompt" && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setShowExplanation((prev) => !prev)}
                  className="inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition-colors cursor-pointer"
                >
                  <HelpCircle className="h-3 w-3" />
                  <span>Why is location required?</span>
                  {showExplanation ? (
                    <ChevronUp className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </button>

                {showExplanation && (
                  <div className="mt-2 rounded-xl border border-neutral-100 bg-neutral-50/80 p-3 text-left text-[11px] text-neutral-500 dark:border-neutral-800 dark:bg-neutral-950/60 dark:text-neutral-400 leading-relaxed">
                    To comply with security and audit compliance standards, administrator mutations (article publishing, role management, user administration) are logged with approximate geographic verification to prevent unauthorized access.
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 space-y-2.5">
              <Button
                type="button"
                onClick={obtainBrowserLocation}
                disabled={requesting}
                className="w-full h-10 gap-2 text-xs font-semibold rounded-xl"
              >
                {requesting ? (
                  <>
                    <RotateCcw className="h-3.5 w-3.5 animate-spin" />
                    <span>Verifying Location...</span>
                  </>
                ) : (
                  <>
                    <MapPin className="h-3.5 w-3.5" />
                    <span>
                      {gateStatus === "denied"
                        ? "Check Again"
                        : gateStatus === "unavailable"
                        ? "Retry Location"
                        : "Allow Location Access"}
                    </span>
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="gap-1.5 text-xs text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
              >
                <LogOut className="h-3.5 w-3.5" />
                <span>Sign out of session</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </LocationContext.Provider>
  );
}
